/**
 * usePeriodMath — derive target / actual / pace + funnel-derived activity
 * targets from `tenants.funnel_defaults` and a revenue target.
 *
 * pace = actual_to_date / (target × elapsed_fraction)
 * green ≥ 0.95, yellow 0.80-0.94, amber 0.60-0.79, red < 0.60.
 */
import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

export interface FunnelDefaults {
  avgDealSize: number;
  closeRateQualToWon: number;
  meetingsPerQual: number;
  outreachPerMeeting: number;
  weeksPerMonth: number;
}

export const DEFAULT_FUNNEL: FunnelDefaults = {
  avgDealSize: 80000,
  closeRateQualToWon: 0.20,
  meetingsPerQual: 5,
  outreachPerMeeting: 5,
  weeksPerMonth: 4.33,
};

const _cache = new Map<number, FunnelDefaults>();

export function useFunnelDefaults(): FunnelDefaults {
  const tenant = getCurrentTenant();
  const tenantId = tenant?.id ?? null;
  const cached = tenantId != null ? _cache.get(tenantId) : null;
  const [funnel, setFunnel] = useState<FunnelDefaults>(cached ?? DEFAULT_FUNNEL);

  useEffect(() => {
    if (tenantId == null) return;
    const c = _cache.get(tenantId);
    if (c) { setFunnel(c); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase()
          .from("tenants")
          .select("funnel_defaults")
          .eq("id", tenantId)
          .limit(1);
        const fd = (data?.[0] as any)?.funnel_defaults;
        if (fd && typeof fd === "object") {
          const m: FunnelDefaults = {
            avgDealSize: Number(fd.avg_deal_size ?? DEFAULT_FUNNEL.avgDealSize),
            closeRateQualToWon: Number(fd.close_rate_qual_to_won ?? DEFAULT_FUNNEL.closeRateQualToWon),
            meetingsPerQual: Number(fd.meetings_per_qual ?? DEFAULT_FUNNEL.meetingsPerQual),
            outreachPerMeeting: Number(fd.outreach_per_meeting ?? DEFAULT_FUNNEL.outreachPerMeeting),
            weeksPerMonth: Number(fd.weeks_per_month ?? DEFAULT_FUNNEL.weeksPerMonth),
          };
          _cache.set(tenantId, m);
          if (!cancelled) setFunnel(m);
        }
      } catch { /* tolerate missing column until migration applied */ }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  return funnel;
}

export interface ActivityTargets {
  closes: number;
  quals: number;
  meetings: number;
  outreach: number;
}

export function activityTargetsForRevenue(revenue: number, f: FunnelDefaults): ActivityTargets {
  const closes = revenue / Math.max(1, f.avgDealSize);
  const quals = closes / Math.max(0.0001, f.closeRateQualToWon);
  const meetings = quals * f.meetingsPerQual;
  const outreach = meetings * f.outreachPerMeeting;
  return {
    closes: Math.round(closes * 100) / 100,
    quals: Math.ceil(quals),
    meetings: Math.ceil(meetings),
    outreach: Math.ceil(outreach),
  };
}

export type PaceColor = "green" | "yellow" | "amber" | "red" | "neutral";
export type PaceLabel = "Ahead" | "On pace" | "Behind" | "Critical" | "—";

export interface PaceResult {
  ratio: number;
  color: PaceColor;
  label: PaceLabel;
}

export function computePace(actualToDate: number, target: number, elapsedFraction: number): PaceResult {
  if (target <= 0 || elapsedFraction <= 0) {
    return { ratio: 0, color: "neutral", label: "—" };
  }
  const expected = target * elapsedFraction;
  const ratio = expected > 0 ? actualToDate / expected : 0;
  if (ratio >= 0.95) return { ratio, color: "green", label: ratio >= 1.05 ? "Ahead" : "On pace" };
  if (ratio >= 0.80) return { ratio, color: "yellow", label: "Behind" };
  if (ratio >= 0.60) return { ratio, color: "amber", label: "Behind" };
  return { ratio, color: "red", label: "Critical" };
}

export function elapsedFraction(start: string | Date, end: string | Date, today: Date = new Date()): number {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const t = today.getTime();
  if (e <= s) return 0;
  return Math.max(0, Math.min(1, (t - s) / (e - s)));
}
